import { Button, CheckIcon, Combobox, Group, useCombobox } from '@mantine/core';
import Image from 'next/image';
import React, { useState } from 'react';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import type { FilterProps } from '@/models/Filters';
import { useGetCollectionsQuery } from '@/utilities/__generated__/graphql';
import { BucketType, createBucketURL } from '@/utilities/createBucketUrl';

interface CollectionsFilterProps extends FilterProps<string[]> {}

export function CollectionsFilter({ onSelected }: CollectionsFilterProps) {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const { data } = useGetCollectionsQuery();

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const options = (data?.Collections ?? []).map((item) => {
    const selected = selectedItems.includes(item.nft_contract_address);
    return (
      <Combobox.Option
        value={item.nft_contract_address}
        key={item.nft_contract_address}
        px={8}
        py={2}
        className={`${selected ? 'bg-primary-surface text-black' : ''}`}
      >
        <Group className="justify-between">
          <div className="flex items-center gap-2">
            <div className="relative flex items-center justify-center">
              {selected && (
                <CheckIcon
                  size={12}
                  className="absolute z-10 m-auto text-primary-main"
                />
              )}
              <Image
                alt={`${item.name} logo`}
                className={`rounded-md ${
                  selected ? 'opacity-20' : 'opacity-100'
                }`}
                src={createBucketURL(
                  BucketType.Collection,
                  item.nft_contract_address
                )}
                height={35}
                width={35}
              />
            </div>
            <p>{item.name}</p>
          </div>
        </Group>
      </Combobox.Option>
    );
  });

  const itemsSelected = selectedItems.length > 0;

  return (
    <>
      <Combobox
        store={combobox}
        width={'auto'}
        zIndex={2}
        keepMounted={true}
        withinPortal={true}
        positionDependencies={[selectedItems]}
        onOptionSubmit={(val) => {
          setSelectedItems((current) => {
            const newItems = current.includes(val)
              ? current.filter((item) => item !== val)
              : [...current, val];
            onSelected(newItems);
            return newItems;
          });
        }}
      >
        <Combobox.Target>
          <Button
            p={0}
            m={4}
            onClick={() => combobox.toggleDropdown()}
            className="flex h-full items-center justify-center bg-transparent hover:bg-transparent"
          >
            <div
              className={`flex w-[176px] items-center justify-center space-x-3 rounded-lg border-[2px] px-6 py-3 text-md font-medium ${
                combobox.dropdownOpened
                  ? 'border-primary-surface bg-primary-main text-white'
                  : `${
                      itemsSelected
                        ? 'border-primary-surface bg-primary-surface text-primary-main'
                        : 'border-neutral-20 bg-white text-black'
                    } hover:border-primary-surface hover:bg-primary-surface`
              }`}
            >
              <p>
                {!itemsSelected
                  ? 'Collections'
                  : `${selectedItems.length} Collection${
                      selectedItems.length > 1 ? 's' : ''
                    }`}
              </p>
              <span>
                {combobox.dropdownOpened ? (
                  <IoIosArrowUp />
                ) : (
                  <IoIosArrowDown />
                )}
              </span>
            </div>
          </Button>
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options className="text-black">{options}</Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </>
  );
}

export default CollectionsFilter;
